import React, { useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { Divider } from '@mui/material';
import CustomLabel from '../../components/common/customLabel';
import useSetTitlePage from '../../hooks/useSetTitlePage';
import Novedades from '../../components/home/novedades/novedades';
import Atajos from '../../components/home/atajos/atajos';
import UltraBanner from '../../components/home/ultraBanner/UltraBanner';
import UltraWelcomeModal from '../../components/home/ultraBanner/UltraWelcomeModal';
import Typography from '@mui/material/Typography';
import { labelTitlePrestador, labelNombreAfiliadoHome, labelNovedadesHome, labelAtajosHome } from '../../global/styles';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { setIsUltra } from '../../redux/features/afiliado/afiliadoSlice';
import * as apiService from '../../../api';
import Popup from '../../components/popUp/popUp';
import { ConfigurationContext } from '../../context/configurationContext';
import colors from '../../../config/colors';

const ULTRA_MODAL_KEY = 'ultraWelcomeShown';

const Home = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isReady, setIsReady] = useState(false);
  const { getAlertaPersonalizada } = useContext(ConfigurationContext);
  const [listaSlider, setListaSlider] = useState([]);
  const [planesAfiliado, setPlanesAfiliado] = useState([]);
  const [ultraModalOpen, setUltraModalOpen] = useState(false);
  const infoAfiliado = useSelector((state) => state.afiliado.infoAfiliado);

  const getAllDashboardSlider = useCallback(async () => {
    try {
      const response = await apiService.ajustes.getAllDashboardSlider();
      setListaSlider(response.data.result);
    } catch (err) {
      console.error('Error fetching dashboard slider:', err);
    }
  }, []);

  const getPlanesAfiliado = useCallback(async () => {
    if (!infoAfiliado.codigoAfiliado) return;

    // Si layout.jsx ya detectó Ultra y lo guardó en Redux + sessionStorage, no volver a llamar la API.
    // Solo verificamos el modal de bienvenida.
    if (sessionStorage.getItem('_isUltra') === '1') {
      const shown = sessionStorage.getItem(ULTRA_MODAL_KEY);
      if (!shown) {
        setUltraModalOpen(true);
        sessionStorage.setItem(ULTRA_MODAL_KEY, '1');
      }
      return;
    }

    try {
      let planes = [];
      try {
        const res = await apiService.afiliado.getPlanAfiliado(infoAfiliado.codigoAfiliado);
        const data = res.data?.result ?? res.data;
        if (data && res.data?.resultType !== 2) {
          planes = Array.isArray(data) ? data : [data];
        }
      } catch { /* ignorar y usar fallback */ }

      if (planes.length === 0) {
        const res = await apiService.afiliado.getAfiliado(infoAfiliado.codigoAfiliado);
        const datosAfiliacion = res.data?.result?.datosAfiliacion ?? [];
        planes = Array.isArray(datosAfiliacion) ? datosAfiliacion : [];
      }
      setPlanesAfiliado(planes);

      const ultraDetectado = planes.some((p) => {
        if (p.esVip === true || p.isVip === true || p.vip === true) return true;
        const nombre = (p.planes_Nombre ?? p.nombre ?? p.nombre_plan ?? p.plan ?? p.descripcion ?? '').toLowerCase();
        return nombre.includes('ultra');
      });
      dispatch(setIsUltra(ultraDetectado));

      if (ultraDetectado) {
        const shown = sessionStorage.getItem(ULTRA_MODAL_KEY);
        if (!shown) {
          setUltraModalOpen(true);
          sessionStorage.setItem(ULTRA_MODAL_KEY, '1');
        }
      }
    } catch (err) {
      console.error('Error fetching planes afiliado:', err);
    }
  }, [infoAfiliado.codigoAfiliado]);

  // Memoizar el título — solo se recrea si cambian isUltra, nombres o apellidos
  const titleHome = useMemo(() => (
    <>
      <CustomLabel style={{
        ...labelTitlePrestador,
        color: infoAfiliado.isUltra ? colors.ultraSilver : undefined,
      }}>
        Hola,{' '}
        <label style={{
          ...labelNombreAfiliadoHome,
          color: infoAfiliado.isUltra ? colors.ultraAccentLight : undefined,
        }}>
          {infoAfiliado.nombres} {infoAfiliado.apellidos}
        </label>
      </CustomLabel>
      <Divider sx={infoAfiliado.isUltra ? { borderColor: colors.ultraAccentBorder } : {}} />
    </>
  ), [infoAfiliado.isUltra, infoAfiliado.nombres, infoAfiliado.apellidos]);

  const { setTitle } = useSetTitlePage(titleHome);

  // Re-dispatch el título cuando isUltra llega de forma asíncrona
  useEffect(() => {
    setTitle(titleHome);
  }, [titleHome]);

  useEffect(() => {
    const userIsInactive = () => {
      const isAuthenticated = localStorage.getItem('autenticado') === 'true';
      if (!isAuthenticated) {
        navigate('/login');
      } else {
        setIsReady(true);
      }
    };

    userIsInactive();
  }, []);

  useEffect(() => {
    if (isReady) {
      getAllDashboardSlider();
      getAlertaPersonalizada();
    }
  }, [isReady]);

  useEffect(() => {
    if (isReady && infoAfiliado.codigoAfiliado) {
      getPlanesAfiliado();
    }
  }, [isReady, infoAfiliado.codigoAfiliado]);

  if (!isReady) {
    return null;
  }

  return (
    <>
      <UltraWelcomeModal
        open={ultraModalOpen}
        onClose={() => setUltraModalOpen(false)}
        nombre={`${infoAfiliado.nombres} ${infoAfiliado.apellidos}`}
      />
      <UltraBanner planes={planesAfiliado} />
      <CustomLabel style={{
        ...labelNovedadesHome,
        color: infoAfiliado.isUltra ? colors.ultraAccentLight : undefined,
      }}>Novedades</CustomLabel>
      <Novedades items={listaSlider} />
      <CustomLabel style={{
        ...labelAtajosHome,
        color: infoAfiliado.isUltra ? colors.ultraAccentLight : undefined,
      }}>Atajos</CustomLabel>
      <Atajos />
      {/* <Popup /> */}
    </>
  );
};

export default Home;
